'use client';

import Icon from '@/components/ui/AppIcon';

export default function PackagesSection() {
  const packages = [
    {
      name: 'Essential',
      description: 'Perfect for small teams and occasional business travel',
      icon: '🏢',
      features: [
        'Up to 10 bookings per month',
        '5% volume discount',
        'Standard check-in/check-out',
        'Email support (24-hour response)',
        'Monthly consolidated invoicing',
        'Basic reporting dashboard'
      ],
      ideal: 'Startups & Small Businesses'
    },
    {
      name: 'Professional',
      description: 'Comprehensive solution for growing organizations',
      icon: '🏛️',
      features: [
        'Up to 50 bookings per month',
        '10% volume discount',
        'Priority check-in/check-out',
        'Dedicated account manager',
        'Flexible payment terms (30 days)',
        'Advanced reporting & analytics',
        'Multi-property access',
        'Approval workflow integration'
      ],
      ideal: 'Mid-Size Companies',
      highlighted: true
    },
    {
      name: 'Enterprise',
      description: 'Tailored solutions for large-scale corporate needs',
      icon: '🏰',
      features: [
        'Unlimited bookings',
        'Custom volume discounts (15%+)',
        'VIP check-in/check-out service',
        'Dedicated account team',
        'Custom payment terms (60 days)',
        'White-label booking portal',
        'Custom SLA agreements',
        'Priority property allocation',
        'Quarterly business reviews'
      ],
      ideal: 'Large Corporations & MNCs'
    }
  ];

  return (
    <section id="packages" className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="CubeIcon" size={20} className="text-primary" />
            <span className="text-primary text-sm font-medium">Tiered Corporate Packages</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-display font-bold text-text-primary mb-6">
            Choose Your Corporate Package
          </h2>
          <p className="text-lg text-text-secondary">
            Flexible accommodation solutions with volume discounts and dedicated support tailored to your organization's size and needs.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {packages?.map((pkg, index) => (
            <div
              key={index}
              className={`rounded-xl p-8 transition-all duration-300 hover:scale-105 ${
                pkg?.highlighted
                  ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-elevated border-2 border-accent'
                  : 'bg-background border-2 border-border hover:border-primary/30 shadow-subtle'
              }`}
            >
              <div className="text-5xl mb-4">{pkg?.icon}</div>
              <h3 className={`text-2xl font-display font-bold mb-2 ${
                pkg?.highlighted ? 'text-white' : 'text-text-primary'
              }`}>
                {pkg?.name}
              </h3>
              <p className={`text-sm mb-6 ${
                pkg?.highlighted ? 'text-white/90' : 'text-text-secondary'
              }`}>
                {pkg?.description}
              </p>
              <div className="space-y-3 mb-6">
                {pkg?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className={pkg?.highlighted ? 'text-accent flex-shrink-0 mt-0.5' : 'text-primary flex-shrink-0 mt-0.5'}
                    />
                    <span className={`text-sm ${
                      pkg?.highlighted ? 'text-white/90' : 'text-text-secondary'
                    }`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              <div className={`pt-6 border-t ${
                pkg?.highlighted ? 'border-white/20' : 'border-border'
              }`}>
                <p className={`text-xs font-medium mb-3 ${
                  pkg?.highlighted ? 'text-white/80' : 'text-text-secondary'
                }`}>
                  IDEAL FOR
                </p>
                <p className={`font-semibold ${
                  pkg?.highlighted ? 'text-white' : 'text-text-primary'
                }`}>
                  {pkg?.ideal}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="#contact"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg shadow-elevated transition-all duration-300 hover:scale-105"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
            <span>Discuss Custom Package</span>
          </a>
        </div>
      </div>
    </section>
  );
}